import dayjs from "dayjs";
import { useEffect, useState } from "react";
import AvailabilityCalendar from "./AvailabilityCalendar";
import YhButton from "../../../../ui/YhButton";
import { CreateAvailability } from "../../Service/Service";
import { openErrorNotification } from "../../../../helpers/methods";

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const DEFAULT_ENABLED = new Set([
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
]);

interface CustomProps {
  nextStep?: () => void | undefined;
  backStep?: () => void | undefined;
  userId: String;
  setUserId?: Function;
  fromSettingOrLogout?: any;
  state?: any;
  setStateData?: any;
}

const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);

const DURATION_OPTIONS = [
  { value: 15, label: "15 minutes" },
  { value: 20, label: "20 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 45, label: "45 minutes" },
  { value: 60, label: "60 minutes" },
];

const BUFFER_OPTIONS = [
  { value: 0, label: "No buffer" },
  { value: 5, label: "5 minutes" },
  { value: 10, label: "10 minutes" },
  { value: 15, label: "15 minutes" },
];

const Availability = (props: CustomProps) => {
  const [weeklyAvailability, setWeeklyAvailability] = useState<any>({});
  const [unavailableDates, setUnavailableDates] = useState<string[]>([]);
  const [is24Hr, setIs24Hr] = useState(false);
  const [saving, setSaving] = useState(false);

  const duration = Number(props.state?.duration) || 15;
  const buffer = Number(props.state?.buffer ?? 5);
  const breakEnabled = props.state?.breakEnabled !== false;
  const breakStart = props.state?.breakStart || "13:30";
  const breakEnd = props.state?.breakEnd || "14:30";

  const startDate = props.state?.startDate
    ? dayjs(props.state.startDate).startOf("day").toDate()
    : dayjs().startOf("day").toDate();

  const endDate = props.state?.endDate
    ? dayjs(props.state.endDate).endOf("day").toDate()
    : dayjs().add(90, "day").endOf("day").toDate();

  const formatTime = (h: number, m: string, use24: boolean) => {
    if (use24) return `${h.toString().padStart(2, "0")}:${m}`;
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    const period = h < 12 ? "AM" : "PM";
    return `${hour12}:${m} ${period}`;
  };

  const TIME_OPTIONS = Array.from({ length: 48 }).map((_, i) => {
    const h = Math.floor(i / 2);
    const m = i % 2 === 0 ? "00" : "30";
    return {
      value: `${h.toString().padStart(2, "0")}:${m}`,
      label: formatTime(h, m, is24Hr),
    };
  });

  const handleTimeChange = (
    day: string,
    field: "start" | "end",
    value: string
  ) => {
    setWeeklyAvailability((prev: any) => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }));
  };

  const getEndTimeOptions = (day: string) => {
    const startTime = weeklyAvailability?.[day]?.start;
    if (!startTime) return TIME_OPTIONS;
    const startIndex = TIME_OPTIONS.findIndex((o) => o.value === startTime);
    return startIndex >= 0 ? TIME_OPTIONS.slice(startIndex + 1) : TIME_OPTIONS;
  };

  useEffect(() => {
    if (props.state?.availability?.length) {
      const mapped: any = {};
      props.state.availability.forEach((item: any) => {
        mapped[item.day] = {
          enabled: item.enabled,
          start: item.availableHours?.start || "10:00",
          end: item.availableHours?.end || "19:00",
        };
      });
      setWeeklyAvailability(mapped);
      return;
    }

    const defaultAvailability = DAYS.reduce((acc: any, day) => {
      acc[day] = {
        enabled: DEFAULT_ENABLED.has(day),
        start: "10:00",
        end: "19:00",
      };
      return acc;
    }, {});
    setWeeklyAvailability(defaultAvailability);
  }, [props.state?.availability]);

  const submitForm = async () => {
    try {
      setSaving(true);
      if (!props.state?.meetingId) {
        openErrorNotification("Meeting not found. Please complete Basic Setup first.");
        return;
      }

      const availability = DAYS.map((day) => ({
        day,
        enabled: weeklyAvailability[day]?.enabled,
        availableHours: {
          start: weeklyAvailability[day]?.start,
          end: weeklyAvailability[day]?.end,
        },
      }));

      const dateOverrides = (props.state?.dateOverrides || []).map((o: any) => ({
        date: o.date,
        inactiveSlots: o.inactiveSlots || [],
      }));

      if (breakEnabled && breakStart >= breakEnd) {
        openErrorNotification("Break end time must be after break start time");
        return;
      }

      const response = await CreateAvailability({
        meetingId: props.state.meetingId,
        startDate,
        endDate,
        unavailableDates,
        availability,
        duration,
        buffer,
        breakEnabled,
        breakStart,
        breakEnd,
        dateOverrides,
      });

      if (!response) {
        openErrorNotification("Failed to save availability. Please try again.");
        return;
      }

      props.nextStep?.();
    } catch (error: any) {
      openErrorNotification(error?.message || "Failed to save availability.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.04)]">
      <div className="min-h-0 flex-1 overflow-auto p-4 sm:p-6">
        {/* Header */}
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F9F0F4] text-[#A02448]">
              <CalendarIcon />
            </span>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-[#111827]">
                Availability
              </h1>
              <p className="mt-0.5 text-sm text-[#6B7280]">
                Configure your weekly recurring schedule and custom date availability
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 rounded-full border border-[#E5E7EB] bg-[#F8FAFC] px-3 py-1.5">
            <span
              className={`text-xs font-semibold ${
                !is24Hr ? "text-[#A02448]" : "text-[#6B7280]"
              }`}
            >
              12HR
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={is24Hr}
              onClick={() => setIs24Hr((v) => !v)}
              className={`relative h-5 w-9 rounded-full transition ${
                is24Hr ? "bg-[#A02448]" : "bg-[#CBD5E1]"
              }`}
            >
              <span
                className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition ${
                  is24Hr ? "left-[18px]" : "left-0.5"
                }`}
              />
            </button>
            <span
              className={`text-xs font-semibold ${
                is24Hr ? "text-[#A02448]" : "text-[#6B7280]"
              }`}
            >
              24HR
            </span>
          </div>
        </div>

        {/* Slot duration + break/lunch */}
        <div className="mb-4 grid grid-cols-1 gap-3 lg:grid-cols-12">
          <div className="rounded-2xl border border-[#EEF0F5] bg-[#FAFBFC] p-4 lg:col-span-4">
            <p className="mb-3 text-sm font-semibold text-[#111827]">
              Slot Duration
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-[#6B7280]">
                  Meeting length
                </label>
                <select
                  value={duration}
                  onChange={(e) =>
                    props.setStateData?.({ duration: Number(e.target.value) })
                  }
                  className="h-10 w-full rounded-lg border border-[#E5E7EB] bg-white px-3 text-sm outline-none focus:border-[#A02448]"
                >
                  {DURATION_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-[#6B7280]">
                  Buffer between slots
                </label>
                <select
                  value={buffer}
                  onChange={(e) =>
                    props.setStateData?.({ buffer: Number(e.target.value) })
                  }
                  className="h-10 w-full rounded-lg border border-[#E5E7EB] bg-white px-3 text-sm outline-none focus:border-[#A02448]"
                >
                  {BUFFER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-[#EEF0F5] bg-[#FAFBFC] p-4 lg:col-span-8">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-[#111827]">
                  Break / Lunch Time
                </p>
                <p className="text-xs text-[#6B7280]">
                  Slots overlapping this window will be blocked
                </p>
              </div>
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-[#374151]">
                <input
                  type="checkbox"
                  checked={breakEnabled}
                  onChange={(e) =>
                    props.setStateData?.({ breakEnabled: e.target.checked })
                  }
                  className="h-4 w-4 accent-[#A02448]"
                />
                Enable break
              </label>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-[#6B7280]">
                  Break start
                </label>
                <select
                  disabled={!breakEnabled}
                  value={breakStart}
                  onChange={(e) =>
                    props.setStateData?.({ breakStart: e.target.value })
                  }
                  className="h-10 w-full rounded-lg border border-[#E5E7EB] bg-white px-3 text-sm outline-none focus:border-[#A02448] disabled:cursor-not-allowed disabled:bg-[#F3F4F6] disabled:text-[#9CA3AF]"
                >
                  {TIME_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-[#6B7280]">
                  Break end
                </label>
                <select
                  disabled={!breakEnabled}
                  value={breakEnd}
                  onChange={(e) =>
                    props.setStateData?.({ breakEnd: e.target.value })
                  }
                  className="h-10 w-full rounded-lg border border-[#E5E7EB] bg-white px-3 text-sm outline-none focus:border-[#A02448] disabled:cursor-not-allowed disabled:bg-[#F3F4F6] disabled:text-[#9CA3AF]"
                >
                  {TIME_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* 3-column layout */}
        <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-12">
          {/* Weekly Schedule */}
          <div className="rounded-2xl border border-[#EEF0F5] bg-[#FAFBFC] p-4 xl:col-span-4">
            <p className="mb-3 text-sm font-semibold text-[#111827]">
              Weekly Schedule
            </p>

            <div className="flex flex-col gap-2">
              {DAYS.map((day) => {
                const isEnabled = !!weeklyAvailability?.[day]?.enabled;
                return (
                  <div
                    key={day}
                    className={`flex flex-wrap items-center gap-2 rounded-xl px-2.5 py-2 ${
                      isEnabled
                        ? "border border-[#E5E7EB] bg-white"
                        : "border border-dashed border-[#E5E7EB] bg-[#F3F4F6]/50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      onChange={(e) =>
                        setWeeklyAvailability((prev: any) => ({
                          ...prev,
                          [day]: { ...prev[day], enabled: e.target.checked },
                        }))
                      }
                      className="h-4 w-4 accent-[#A02448]"
                    />
                    <span
                      className={`w-[78px] shrink-0 text-xs ${
                        isEnabled
                          ? "font-semibold text-[#111827]"
                          : "font-medium text-[#9CA3AF]"
                      }`}
                    >
                      {day}
                    </span>
                    <div className="flex min-w-0 flex-1 items-center gap-1.5">
                      <select
                        disabled={!isEnabled}
                        value={weeklyAvailability?.[day]?.start || ""}
                        onChange={(e) =>
                          handleTimeChange(day, "start", e.target.value)
                        }
                        className="h-9 min-w-0 flex-1 rounded-lg border border-[#E5E7EB] bg-white px-2 text-xs outline-none focus:border-[#A02448] disabled:cursor-not-allowed disabled:bg-[#F3F4F6] disabled:text-[#9CA3AF]"
                      >
                        {TIME_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      <span className="shrink-0 text-[11px] text-[#9CA3AF]">
                        to
                      </span>
                      <select
                        disabled={!isEnabled}
                        value={weeklyAvailability?.[day]?.end || ""}
                        onChange={(e) =>
                          handleTimeChange(day, "end", e.target.value)
                        }
                        className="h-9 min-w-0 flex-1 rounded-lg border border-[#E5E7EB] bg-white px-2 text-xs outline-none focus:border-[#A02448] disabled:cursor-not-allowed disabled:bg-[#F3F4F6] disabled:text-[#9CA3AF]"
                      >
                        {getEndTimeOptions(day).map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>

            {breakEnabled ? (
              <div className="mt-3 rounded-xl border border-[#DDD6FE] bg-[#F5F3FF] px-3 py-2.5 text-[#5B21B6]">
                <p className="text-[11px] font-medium leading-relaxed">
                  Break timings are set to {breakStart} – {breakEnd}. These hours
                  will be blocked from your availability.
                </p>
              </div>
            ) : (
              <div className="mt-3 rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-[#6B7280]">
                <p className="text-[11px] font-medium leading-relaxed">
                  Break/lunch blocking is disabled. Slots will generate across
                  the full available hours.
                </p>
              </div>
            )}
          </div>

          {/* Calendar + Slots */}
          <div className="overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white xl:col-span-8">
            <AvailabilityCalendar
              weeklyAvailability={weeklyAvailability}
              unavailableDates={unavailableDates}
              setUnavailableDates={setUnavailableDates}
              startDate={startDate}
              endDate={endDate}
              state={props.state}
              setStateData={props.setStateData}
              duration={duration}
              buffer={buffer}
              breakEnabled={breakEnabled}
              breakStart={breakStart}
              breakEnd={breakEnd}
            />
          </div>
        </div>
      </div>

      {/* Sticky footer — Go Back left, Continue right */}
      <div className="sticky bottom-0 z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-[#EEF0F5] bg-white px-5 py-4 sm:px-6">
        <YhButton
          variant="secondary"
          fullWidth={false}
          onClick={() => props.backStep?.()}
          className="!w-auto"
        >
          Go Back
        </YhButton>
        <YhButton
          variant="primary"
          fullWidth={false}
          loading={saving}
          onClick={submitForm}
          className="!w-auto"
        >
          Save & Continue →
        </YhButton>
      </div>
    </div>
  );
};

export default Availability;
